import { useEffect, useState, useContext, useRef } from "react";
import { CircularProgress, Box, TextField, InputAdornment, IconButton, Chip, Avatar, Pagination } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import CustomizedTables, { StyledTableCell, StyledTableRow } from "../../../Component/UI/Table/Table";
import Modal from "../../../Component/Modal/Modal";
import classes from "./HistoryV2.module.css";
import { AuthContext } from "../../../context/auth-context";
import useHistory from "./useHistory";
import { DiscoveryContext } from "../discovery-context";
import api from "../../../BrandOnBoarding/api";
import useHistoryV1 from "../../Discovery/History/useHistory";
import { LLM_OPTIONS } from "../../../utils/constant";
import { updateValidatedSources } from "../../../utils/helpers";
import BrandAvatar from "../../../Component/Avatar/Avatar"
import { getMetricsRecord,OVERVIEW_LLM_LABELS } from "../../../utils/constant"
import { ENTITY_LABELS, getEntityLabel } from "../../../utils/labelMapping";

const PAGE_LIMIT = 5;

// signalStrength's "pending validation" sources can arrive as a plain URL string
// (old/legacy), a per-URL {url, count, http_status} object, or an already-merged
// {url: count, ...} map (v4's live socket push) — normalize each to a single-key
// {url: count} entry (or pass an already-merged map through as-is) so the reducer's
// merge-by-spread logic works regardless of which shape the backend sent.
function toOldSourcesEntry(entry) {
  if (typeof entry === "string") return { [entry]: 1 };
  if (entry && typeof entry === "object" && "url" in entry) {
    return { [entry.url]: entry.count ?? 1 };
  }
  return entry;
}

export default function HistoryV2({
  filter = "brand",
  setCancelTransaction = () => { },
  startPolling = () => { },
  isHistoryRefresh = false,
  setIsHistoryRefresh = () => { },
  stopPolling = () => { },
  open,
  onClose,
  onDiscoveryModeChange = () => { },
  isBaselineDiscoveryEnabled = false,
  onBaselineHtmlReportChange = () => { },
}) {
  const [filterOption, setFilterOption] = useState(filter);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [oldPage, setOldPage] = useState(1);
  const [isUsingOldHistory, setIsUsingOldHistory] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTotalCount, setSearchTotalCount] = useState(0);
  const [searchTotalPages, setSearchTotalPages] = useState(0);
  const [isSearchMode, setIsSearchMode] = useState(false);

  const { authUserEmail } = useContext(AuthContext);
  const discoveryCtx = useContext(DiscoveryContext);
  const { user, selectedCustomerId } = useContext(AuthContext);
  const transactionIdRef = useRef();
  const debounceTimerRef = useRef(null);
  const webSocketBaseURL = process.env.REACT_APP_ORCHESTRATOR_BASE_URL;
 const metricsRecord = getMetricsRecord();
  const {
    history,
    setHistory,
    loading,
    getHistory,
    deleteHistory,
    error,
    hasReachedEnd,
    setHasReachedEnd,
    setLoading,
    totalCount,
    totalPages,
  } = useHistory(filterOption);

  const { deleteHistory: deleteHistoryV1 } = useHistoryV1(authUserEmail, filterOption);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 400);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedSearchQuery.trim()) {
        setIsSearchMode(false);
        setSearchResults([]);
        setSearchTotalCount(0);
        setSearchTotalPages(0);
        return;
      }

      setIsSearchMode(true);
      setSearchLoading(true);

      try {
        const response = await api.get(
          `${webSocketBaseURL}/search-history/discovery/${filterOption}/${selectedCustomerId}/${user?.user_role?.user_id}`,
          {
            params: {
              search: debouncedSearchQuery,
              page: page,
              limit: PAGE_LIMIT
            }
          }
        );

        const { data = [], totalCount = 0, totalPages = 0 } = response.data;
        discoveryCtx.updateTransactionHistory(data)
        // No transformation needed - search API returns same format as history API
        setSearchResults(data);
        setSearchTotalCount(totalCount);
        setSearchTotalPages(totalPages);
      } catch (error) {
        console.error("Search error:", error);
        setSearchResults([]);
        setSearchTotalCount(0);
        setSearchTotalPages(0);
      } finally {
        setSearchLoading(false);
      }
    };

    performSearch();
  }, [debouncedSearchQuery, page, filterOption, selectedCustomerId, user?.user_role?.user_id, webSocketBaseURL]);

  useEffect(() => {
    setFilterOption(filter);
    setIsUsingOldHistory(false);
    setOldPage(1);
    setHistory([]);
    setHasReachedEnd(false);
    setPage(1);
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setIsSearchMode(false);
    setSearchResults([]);
  }, [filter]);

  useEffect(() => {
    setPage(1);
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setIsSearchMode(false);
    setSearchResults([]);
  }, [isHistoryRefresh]);

  useEffect(() => {
    if (discoveryCtx.state.transactionId) {
      transactionIdRef.current = discoveryCtx.state.transactionId;
    }
  }, [discoveryCtx.state.transactionId]);

  useEffect(() => {
    if (isSearchMode) return;

    if (!searchQuery.trim()) {
      setLoading(true);
      async function fetchNewHistory() {
        const newHistory = await getHistory(page, PAGE_LIMIT);

        if (newHistory?.length > 0) {
          setHistory(newHistory);
          setHasReachedEnd(false);
          setIsUsingOldHistory(false);
        } else {
          setHasReachedEnd(false);
          setOldPage(1);
          setIsUsingOldHistory(true);
        }
      }

      setTimeout(async () => {
        await fetchNewHistory();
        setLoading(false);
      }, 300);
    }
  }, [page, filterOption, isHistoryRefresh, isSearchMode]);

  useEffect(() => {
    if (!isUsingOldHistory) return;
    setLoading(true);
    async function fetchOldHistory() {
      const oldHistory = await getOldHistory(oldPage, PAGE_LIMIT);
      if (oldHistory?.length > 0) {
        setHistory(oldHistory);
        if (oldHistory.length < PAGE_LIMIT) {
          setHasReachedEnd(true);
        }
      } else {
        setHasReachedEnd(true);
      }
    }

    setTimeout(async () => {
      await fetchOldHistory();
      setLoading(false);
    }, 300);
  }, [oldPage, isUsingOldHistory]);

  async function getOldHistory(page = 1, limit = 5) {
    try {
      setLoading(true);
      const response = await api.get(
        `/users/discovery?user_email=${encodeURIComponent(authUserEmail)}&page=${page}&limit=${limit}&type=${filterOption}`
      );
      const data = response.data;
      return data.data.map(item => ({
        ...item,
        market: item?.json_data?.market
      }));
    } catch (error) {
      if (error.name === "CanceledError") {
        return;
      }
    }
  }

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setIsSearchMode(false);
    setSearchResults([]);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);

    const tableWrapper = document.querySelector(`.${classes.tableWrapper}`);
    if (tableWrapper) {
      tableWrapper.scrollTop = 0;
    }
  };

  const refreshHistory = () => {
    setHistory([]);
    setPage(1);
    setOldPage(1);
    setIsUsingOldHistory(false);
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setIsSearchMode(false);
    setSearchResults([]);
    setIsHistoryRefresh(prev => !prev);
  };

  const deleteHistoryItemHandler = async () => {
    if (itemToDelete) {
      const deletedId = String(itemToDelete.transactionId ?? "");
      const activeIds = [discoveryCtx.state?.id, discoveryCtx.state?.transactionId]
        .filter(Boolean)
        .map(String);
      const isDeletingActiveRecord = activeIds.includes(deletedId);

      if (isDeletingActiveRecord) {
        setCancelTransaction(true);
        stopPolling();
        discoveryCtx.resetState();
      }

      try {
        if (itemToDelete.json_data) {
          await deleteHistoryV1(itemToDelete.id);
        } else {
          await deleteHistory(itemToDelete.transactionId);
        }
      } catch (_) {}

      setShowDeleteDialog(false);
      setItemToDelete(null);
      setHistory([]);
      setPage(1);
      setIsHistoryRefresh(prev => !prev);
      setIsUsingOldHistory(false);
      setOldPage(1);
      setSearchQuery("");
      setDebouncedSearchQuery("");
      setIsSearchMode(false);
      setSearchResults([]);
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteDialog(true);
  };

  let currentBrandSnippets = [];
  let currentBrandThemes = [];
  const coreAttributeMap = {};

  const handleSocketData = (socketResponse, transaction, showOldSourcesRecord) => {
    if (!socketResponse || typeof socketResponse !== "object") {
      console.error("Invalid socket response:", socketResponse);
      return;
    }

    let { type, request, response, category, llm, status, isValidated } = socketResponse;
    let transactionId = transaction?.transactionId;

    if (!type || !request) {
      console.error("Incomplete socket response:", response);
      return;
    }
    if (status !== "COMPLETED") {
      return false;
    }
    const currentContextTxnId = transactionIdRef.current;
    if (transactionId !== currentContextTxnId) return;

    const onlyIfValidated = (data, empty = Array.isArray(data) ? [] : {}) => {
      if (showOldSourcesRecord) {
        if (Array.isArray(data)) return data;
        if (data == null) return [];
        return [data];
      }
      return isValidated ? data : empty;
    };

    console.log("DiscoveryHistoryResponse", socketResponse);
    switch (type) {
      case "rank":
        if (!response) return console.warn("Missing response for rank");
        response = { ...response, request_id: socketResponse.id, isValidated };
        const sources = onlyIfValidated(response?.sources?.rank || [], []);
        discoveryCtx.updateSources({ sources, showOldSourcesRecord,type:metricsRecord[type] });
        discoveryCtx.updateRankScores(response);
        break;

      case "sentiment":
        if (!response || !transaction) return console.warn("Missing response/Transaction for sentiment");
        const entity = { ...response?.data }?.[0];
        const sentimentSources = onlyIfValidated(response?.sources?.sentiment || [], []);
        const updatedExtras = !showOldSourcesRecord ? updateValidatedSources(response.extras) : response.extras;
        const brandRes = response?.error
          ? { error: "something went wrong" }
          : {
            name: entity?.name || "Unknown",
            favorability: entity?.score ?? 0,
            extras: updatedExtras,
            sentimentSources,
            isValidated
          };

        let index = -1;
        try {
          transaction.competitor?.[request.category]?.forEach((item, idx) => {
            if (item.name?.toLowerCase() == entity?.name?.toLowerCase()) {
              index = idx;
            }
          });
          if (transaction.brand === entity?.name) index = -1;
        } catch (err) {
          console.warn("Error calculating sentiment index:", err);
        }

        discoveryCtx.updateBrandScores({
          data: brandRes,
          index: index + 1,
          sentimentSources,
          isValidated
        });
        discoveryCtx.updateSources({ sources: sentimentSources, showOldSourcesRecord,type:metricsRecord[type] });
        break;

      case "signalStrength":
        if (!response) return console.warn("Missing response for signalStrength");
        const negativeAttributes = response?.aggregatedNegatives || [];
        const positiveAttributes = response?.aggregatedPositives || [];

        let sourcesPositiveArray = onlyIfValidated(
          response?.aggregatedPositiveSources || [],
          []
        );
        let sourcesNegativeArray = onlyIfValidated(
          response?.aggregatedNegativeSources || [],
          []
        );
        if (showOldSourcesRecord) {
          sourcesPositiveArray = sourcesPositiveArray.map(toOldSourcesEntry);
          sourcesNegativeArray = sourcesNegativeArray.map(toOldSourcesEntry);
        }

        discoveryCtx.updatePositiveNegativeAttributes({
          name: socketResponse.brand,
          positives: positiveAttributes.slice(0, 10),
          negatives: negativeAttributes.slice(0, 10),
          baseResponses: response?.baseResponses || {}
        });

        discoveryCtx.updateSources({ sources: sourcesPositiveArray, showOldSourcesRecord, type: "Positive Attributes" });
        discoveryCtx.updateSources({ sources: sourcesNegativeArray, showOldSourcesRecord, type: "Negative Attributes" });
        break;
      case "categoryDimension":
        if (!response) return console.warn("Missing response for categoryDimension");
        const normalize = (src) =>
          Array.isArray(src)
            ? onlyIfValidated(src, [])
            : onlyIfValidated(src ? [src] : [], []);

        const sourcesCategory = normalize(response?.sources?.category);
        const sourcesDim = normalize(response?.sources?.dimension);
        const sourcesCategoryDim = [...sourcesCategory, ...sourcesDim];

        discoveryCtx.updateCategoryDimensions({
          data: response?.data || [],
          summary: response?.summary || "",
          sources: sourcesCategory,
          baseResponses: { ...(response?.baseResponses || {}), multi_prompting: response?.multi_prompting || false, num_runs: response?.num_runs },
          isValidated,
          request_id: socketResponse.id,
        });
        discoveryCtx.updateSources({ sources: sourcesCategoryDim, showOldSourcesRecord,type:metricsRecord[type] });
        break;

      case "taxonomy":
        if (!response) return console.warn("Missing response for taxonomy");
        response = { ...response, isValidated, request_id: socketResponse.id };
        discoveryCtx.updatedTaxonomies(response);
        break;

      case "core-attribute":
        if (!response || !category || !llm) return console.warn("Missing data for core-attribute");
        const key = `${llm}__${category}`;

        if (!coreAttributeMap[key]) {
          coreAttributeMap[key] = [];
        }

        coreAttributeMap[key].push({ ...response, _socket_request_id: socketResponse.id });
        let competitors = transaction?.competitor?.[category].map((item) => item.name);
        let brand = transaction?.brand;
        if (competitors) {
          competitors.unshift(brand);
        } else {
          competitors = [];
        }
       const expectedCount = competitors?.length ?? 0;
          console.log("CoreAttributeData", coreAttributeMap[key])

        if (coreAttributeMap[key].length === expectedCount) {
          const group = coreAttributeMap[key];
          let coreAttributeSummary = "";
          const products = [];
          const sources = [];
          const category_averages = group[0]?.category_averages;
          for (const res of group) {
            if (!res?.error && Array.isArray(res?.products)) {
              const resBrandName = (res.brandName || res.products?.[0]?.name || "").toLowerCase().trim();
              if (resBrandName === brand.toLowerCase().trim()) {
                coreAttributeSummary = res.products?.[0]?.summary || "";
              }
              const productSources = onlyIfValidated(res?.sources?.product || [], []);

              products.push({
                ...res.products?.[0],
                brandName: res.brandName,
                sources: productSources,
                baseResponses: res.baseResponses || null,
                multi_prompting: res.multi_prompting || false,
                num_runs: res.num_runs,
                request_id: res._socket_request_id,
              });

              if (showOldSourcesRecord || isValidated) {
                const normalize = (src) =>
                  Array.isArray(src) ? src : src ? [src] : [];

                const categorySources = normalize(res?.sources?.category);
                const productSources = normalize(res?.sources?.product);
                sources.push(...categorySources, ...productSources);
              }
            }
          }

          let sortedProducts = [];
          let baseResponsesArray = [];

          for (const company of competitors) {
            const normalizedCompany = company.toLowerCase().replace(/\s+/g, "");
            for (const product of products) {
              const normalizedProductName = (product?.brandName || product?.name || "")?.toLowerCase()?.replace(/\s+/g, "");
              if (normalizedProductName === normalizedCompany) {
                // Extract baseResponses and add to separate array
                if (product.baseResponses && typeof product.baseResponses === 'object') {
                  const baseResObj = product.baseResponses;
                  if (baseResObj.baseResponses && Array.isArray(baseResObj.baseResponses)) {
                    const firstItem = baseResObj.baseResponses[0];
                    const isPerAttributeMulti = firstItem?.attribute != null && Array.isArray(firstItem?.runs);
                    const isFlatRunMulti = firstItem?.run != null && firstItem?.baseRes != null && baseResObj.baseResponses.length > 1;
                    const isMultiPrompting = product.multi_prompting === true || isPerAttributeMulti || isFlatRunMulti;
                    baseResponsesArray.push({
                      brandName: product.brandName,
                      basePrompt: baseResObj.basePrompt || "",
                      multi_prompting: isMultiPrompting,
                      num_runs: product.num_runs || (isPerAttributeMulti ? firstItem?.runs?.length : undefined),
                      baseResponse: isPerAttributeMulti
                        ? baseResObj.baseResponses
                        : isFlatRunMulti
                          ? baseResObj.baseResponses
                          : baseResObj.baseResponses.map(item => ({ baseRes: item.baseRes || "" }))
                    });
                  }
                }

                // Remove baseResponses from product before adding to sortedProducts
                const { baseResponses, ...productWithoutBaseResponses } = product;
                sortedProducts.push(productWithoutBaseResponses);
                break;
              }
            }
          }

          discoveryCtx.updateCoreAttributes({
            category_averages,
            products: sortedProducts,
            summary: coreAttributeSummary,
            baseResponses: baseResponsesArray,
            isValidated,
          });
          discoveryCtx.updateSources({ sources, showOldSourcesRecord, type: metricsRecord[type] });
          delete coreAttributeMap[key];
        }
        break;

      case "description":
        if (!response?.data) return console.warn("Missing response.data for description");
        discoveryCtx.updateBrandDescription(response.data);
        break;

      case "theme":
      case "snippet": {
        const isTheme = type === "theme";
        const dataSnippet = response?.data?.llm ? response?.data : response?.data?.data;
        if (!Array.isArray(response?.data?.data)) {
          console.warn(`${type} response is not a valid array:`, dataSnippet);
          return;
        }

        if (!llm || !category) {
          console.warn("Missing llm/category for", type);
          return;
        }

        const targetStore = isTheme ? currentBrandSnippets : currentBrandThemes;
        const contextMethod = isTheme
          ? discoveryCtx.updateBrandSnippets
          : discoveryCtx.updatedBrandThemes;

        targetStore.push(dataSnippet);
        contextMethod({ res: targetStore });
        break;
      }

      case "non_branded_questions":
        if (!response) return console.warn("Missing response for non_branded_questions");
        discoveryCtx.updateNonBrandedQuestionsResults({
          questionsType: response?.questionsType || "open-ended",
          meta: response?.extras?.nonBrandedQuestionsMeta || [],
          baseResponses: response?.extras?.completeBaseRes?.nonBrandedQuestionBaseRes || [],
        });
        break;

      default:
        console.warn(`Unhandled socket data type: ${type}`);
    }
  };

  const getTransactionData = async (transactionRecord) => {
    onClose();
    let transactionId = transactionRecord.transactionId;
    const showOldSourcesRecord = transactionRecord.sourceValidationStatus === "PENDING";
    discoveryCtx.resetState();
    stopPolling();
    discoveryCtx.updateTransactionId(transactionId);
    discoveryCtx.updateEntity(transactionRecord.brand);

    let competitors = transactionRecord.competitor[transactionRecord.category];
    competitors = [
      {
        name: transactionRecord.brand,
        domain: transactionRecord.brandDomainName,
        variants: [],
        display_name: transactionRecord.brand
      },
      ...competitors
    ];
    discoveryCtx.updatedCompetitors(competitors);
    discoveryCtx.updateMarket(transactionRecord.market.join(","));
    discoveryCtx.updateTransactionStatus(transactionRecord.status);
    discoveryCtx.updateSourceValidationStatus(transactionRecord.sourceValidationStatus);
    discoveryCtx.updateCategory(transactionRecord.category[0]);
    discoveryCtx.updateCategoryType(transactionRecord.categoryType);
    discoveryCtx.updateLanguage(discoveryCtx.languageList?.find(l => l.name === transactionRecord.language)?.value || "en");
    discoveryCtx.updateLLM(transactionRecord.llm[0]);
    onDiscoveryModeChange(transactionRecord?.discoveryMode || "full");
    onBaselineHtmlReportChange(transactionRecord?.is_baseline_html_reporting_enabled || false);
    discoveryCtx.updateName(`${transactionRecord.brand} ${ENTITY_LABELS.brand} Overview`);
    discoveryCtx.updateId(transactionId);
    discoveryCtx.updatedLaunched(true);
    discoveryCtx.updateCustomTemplateID(transactionRecord.userMetricSettingId || null);
    discoveryCtx.updateCustomIndexScoreType(transactionRecord.custom_index_score_type || null);
    discoveryCtx.updateRankCompetitiveSet(transactionRecord.rank_competitive_set || []);
    discoveryCtx.updateRankMethodology(transactionRecord.rank_methodology || "by_category");
    if (transactionRecord.keywords?.length > 0) discoveryCtx.updateKeywords(transactionRecord.keywords);
    discoveryCtx.updateIsBatchMode(false); // authoritative value set below once request_object loads
    discoveryCtx.updateIsNonBrandedQuestions(false); // authoritative value set below once request_object loads
    discoveryCtx.updateIsFactBasedQuestions(false); // authoritative value set below once request_object loads

    if (transactionRecord.status === "COMPLETED") {
      discoveryCtx.updateLoading(false);
    } else {
      discoveryCtx.updateLoading(true);
    }

    setTimeout(() => {
      api
        .get(`${webSocketBaseURL}/transaction/discovery/${user?.user_role?.user_id}/${transactionId}`)
        .then((response) => {
          const { totalCount, completedTaskCount, transactionStatus, task = [], userMetricSettingId, user_metric_setting_object } = response?.data;
          discoveryCtx.updateStatus({ totalCount, completedTaskCount });

          if (completedTaskCount === totalCount) {
            discoveryCtx.updateLoading(false);
          } else if (transactionStatus === "RUNNING") {
            startPolling(transactionId, discoveryCtx.type);
          }

          if (userMetricSettingId) discoveryCtx.updateCustomTemplateID(userMetricSettingId);
          discoveryCtx.setMetricSettingObj(user_metric_setting_object ?? {});
          const reqObj = task[0]?.request_object;
          if (reqObj?.custom_index_score_type) discoveryCtx.updateCustomIndexScoreType(reqObj.custom_index_score_type);
          if (reqObj?.rank_competitive_set?.length) {
            discoveryCtx.updateRankCompetitiveSet(reqObj.rank_competitive_set);
          }
          if (reqObj?.rank_methodology) discoveryCtx.updateRankMethodology(reqObj.rank_methodology);
          if (reqObj?.keywords?.length > 0) discoveryCtx.updateKeywords(reqObj.keywords);
          discoveryCtx.updateIsBatchMode(!!reqObj?.llmBatchConfig && Object.keys(reqObj.llmBatchConfig).length > 0);
          discoveryCtx.updateIsNonBrandedQuestions(!!reqObj?.is_non_branded_questions);
          discoveryCtx.updateIsFactBasedQuestions(!!reqObj?.is_fact_based_questions);

          for (const record of task) {
            handleSocketData(record, transactionRecord, showOldSourcesRecord);
          }
        });
    }, 1000);
  };

  const getOldTransactionData = (item) => {
    onClose();
    discoveryCtx.resetState();
    stopPolling();

    let updatedItem = { ...item.json_data, id: item.id };
    let coreAttributeSummary = updatedItem?.core_attributes?.summary ?? "";

    if (!coreAttributeSummary) {
      updatedItem?.core_attributes &&
        updatedItem?.core_attributes?.products &&
        updatedItem?.core_attributes?.products.map((product) => {
          coreAttributeSummary += product.summary;
          coreAttributeSummary += "\n\n";
        });
    }

    discoveryCtx.updateEntireState({
      ...updatedItem,
      core_attributes: {
        ...updatedItem.core_attributes,
        summary: coreAttributeSummary,
      },
      is_launched: true,
      loading: false,
    });
    discoveryCtx.updateTransactionHistory([]);
  };

  const handleViewDetails = (item) => {
    if (item.json_data) {
      getOldTransactionData(item);
    } else {
      getTransactionData(item);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      COMPLETED: { color: "success", label: "Completed" },
      RUNNING: { color: "warning", label: "In Progress" },
      PENDING: { color: "warning", label: "In Progress" },
      INITIATED: { color: "warning", label: "In Progress" },
      RETRY: { color: "error", label: "Failed" },
      FAILED: { color: "error", label: "Failed" },
    };

    const config = statusConfig[status] || { color: "default", label: status };

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        className={classes.statusChip}
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const discoveryType = discoveryCtx.type ?? "brand";

  const headers = [
    { id: "brand", name: `${getEntityLabel(discoveryType)}`, sorting: false },
    { id: "category", name: "Category", sorting: false },
    { id: "llm", name: "LLM Model", sorting: false },
    { id: "market", name: "Market", sorting: false },
    { id: "date", name: "Date Run", sorting: false },
    // { id: "indexScore", name: "Index Score", sorting: false },
    ...(isBaselineDiscoveryEnabled ? [{ id: "discoveryType", name: "Discovery Type", sorting: false }] : []),
    { id: "status", name: "Status", sorting: false },
    { id: "actions", name: "Actions", sorting: false },
  ];

  const renderRow = (row, index) => {
    const isOldHistory = !!row.json_data;
    const isCompleted = row.status === "COMPLETED";
    const isFailed = row.status === "RETRY" || row.status === "FAILED";
    const isSourceValidating = !isOldHistory &&
      row?.sourceValidationStatus === "RUNNING" &&
      ["COMPLETED", "RETRY"].includes(row?.status);

    const brandName = isOldHistory ? (row?.name || row.entity) : row.brand;
    const categoryName = isOldHistory ? row?.category : row.category?.[0];
    const llmValue = isOldHistory ? row?.llm : row?.llm?.[0];
    const marketName = isOldHistory ? row?.market : row.market;
    const dateValue = isOldHistory ? row?.date : row?.start_time;

    const startDateTime = formatDateTime(dateValue);
    const llmName = OVERVIEW_LLM_LABELS[llmValue] || "";

    return (
      <StyledTableRow key={row.transactionId || row.id || index}>
        <StyledTableCell>
          <Box className={classes.brandCell}>
            <BrandAvatar
              domainName={row.brandDomainName}
              brandName={brandName}
              className={classes.brandAvatar}
            />
            <Box>
              <div className={classes.brandName} title={brandName}>
                {brandName || "Unknown"}
                {/* Shared-by badge hidden per client request
                {row?.is_shared && (
                  <Chip
                    label={row?.owner_name ? `Shared by ${row.owner_name}` : "Shared with you"}
                    size="small"
                    sx={{ ml: 1, fontSize: "10px", height: "18px", backgroundColor: "#ede9fe", color: "#6d28d9" }}
                  />
                )} */}
              </div>
              <div className={classes.brandSubtext}>
                {discoveryType === "brand" ?`${ENTITY_LABELS.brand} Overview`:`${ENTITY_LABELS.product} Overview`}
              </div>
              {isSourceValidating && (
                <div className={classes.validationBadge}>Sources Validating...</div>
              )}
            </Box>
          </Box>
        </StyledTableCell>

        <StyledTableCell>
          {categoryName || "-"}
        </StyledTableCell>

        <StyledTableCell>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1.5,
              py: 0.5,
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              backgroundColor: "#f9fafb",
              fontSize: "12px",
              fontWeight: 500,
              color: "#374151",
            }}
          >
            {llmName}
          </Box>
        </StyledTableCell>

        <StyledTableCell>
          {marketName === "GeneralMarket" || marketName === "General Market"
            ? "General Market"
            : marketName || "General Market"}
        </StyledTableCell>

        <StyledTableCell title={startDateTime}>{formatDate(dateValue)}</StyledTableCell>

        {/* <StyledTableCell>
          {row.custom_index_score_type || "RBI"}
        </StyledTableCell> */}

        {isBaselineDiscoveryEnabled && (
          <StyledTableCell>
            <Chip
              label={row.discoveryMode === "baseline" ? "Discovery Test" : "Full"}
              size="small"
              sx={{
                fontSize: "11px",
                height: "22px",
                backgroundColor: row.discoveryMode === "baseline" ? "#ede9fe" : "#e0f2fe",
                color: row.discoveryMode === "baseline" ? "#6d28d9" : "#0369a1",
                fontWeight: 500,
              }}
            />
          </StyledTableCell>
        )}

        <StyledTableCell>
          {isOldHistory ? (
            <Chip
              label="Completed"
              color="success"
              size="small"
              className={classes.statusChip}
            />
          ) : (
            getStatusChip(row.status)
          )}
        </StyledTableCell>

        <StyledTableCell>
          <Box className={classes.actionButtons}>
            <IconButton
              size="small"
              className={classes.actionIcon}
              onClick={() => handleViewDetails(row)}
              title={isFailed ? "Retry" : "View Details"}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              className={`${classes.actionIcon} ${classes.deleteIcon}`}
              onClick={() => handleDeleteClick(row)}
              title="Delete"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </StyledTableCell>
      </StyledTableRow>
    );
  };

  // Use search results when in search mode, otherwise use normal history
  const displayData = isSearchMode ? searchResults : history;
  const currentLoading = isSearchMode ? searchLoading : loading;
  const currentTotalCount = isSearchMode ? searchTotalCount : totalCount;
  const currentTotalPages = isSearchMode ? searchTotalPages : totalPages;

  const calculatedTotalPages = currentTotalPages || Math.ceil((currentTotalCount || 0) / PAGE_LIMIT);

  const startIndex = (page - 1) * PAGE_LIMIT;
  const endIndex = Math.min(page * PAGE_LIMIT, currentTotalCount || displayData.length);

  return (
    <>
      <Modal
        handleClose={onClose}
        open={open}
        title="Discovery History"
        width="1500"
        showActions={false}
      >
        <Box className={classes.historyContainer}>
          <Box className={classes.headerContainer}>
            <TextField
              placeholder={`Search by ${ENTITY_LABELS.brand}/${ENTITY_LABELS.product}, category, LLM, market`}
              value={searchQuery}
              onChange={handleSearchChange}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className={classes.searchIcon} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      edge="end"
                      className={classes.clearButton}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              className={classes.searchField}
            />
            {/* <IconButton
              onClick={refreshHistory}
              className={classes.refreshButton}
              title="Refresh History"
            >
              <RefreshIcon />
            </IconButton> */}
          </Box>

          <Box className={classes.tableWrapper}>
            {currentLoading && displayData.length === 0 ? (
              <Box className={classes.loadingContainer}>
                <CircularProgress />
              </Box>
            ) : (
              <CustomizedTables
                headers={headers}
                data={displayData}
                render={renderRow}
                elevation={0}
                size="medium"
              />
            )}
          </Box>

          {(currentTotalCount > 0 || displayData.length > 0) && (
            <Box className={classes.paginationContainer}>
              <div className={classes.resultsText}>
                {currentLoading ? (
                  <>
                    Loading...
                  </>
                ) : (
                  <>
                    Showing{" "}
                    <span className={classes.boldText}>
                      {startIndex + 1}
                    </span>{" "}
                    to{" "}
                    <span className={classes.boldText}>
                      {endIndex}
                    </span>{" "}
                    of{" "}
                    <span className={classes.boldText}>
                      {currentTotalCount || 0}
                    </span>{" "}
                    results
                    {isSearchMode && (
                      <span className={classes.searchModeIndicator}> (filtered)</span>
                    )}
                  </>
                )}
              </div>
              {calculatedTotalPages > 1 && (
                <Pagination
                  count={calculatedTotalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  className={classes.pagination}
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                  disabled={currentLoading}
                />
              )}
            </Box>
          )}

          {error && displayData.length > 0 && (
            <Box className={classes.errorContainer}>
              <p className={classes.errorText}>{error}</p>
            </Box>
          )}

          {!currentLoading && !error && displayData.length === 0 && (
            <Box className={classes.emptyState}>
              <p>{isSearchMode ? "No results found for your search." : "Nothing here."}</p>
            </Box>
          )}

          {error && displayData.length === 0 && (
            <Box className={classes.errorState}>
              <h3>Error</h3>
              <p>{error}</p>
            </Box>
          )}
        </Box>
      </Modal>

      <Modal
        handleClose={() => {
          setShowDeleteDialog(false);
          setItemToDelete(null);
        }}
        handleConfirm={deleteHistoryItemHandler}
        open={showDeleteDialog}
        title="Delete History"
        showActions
        confirmBtnText="Delete"
        confirmBtnColor="error"
      >
        <div className={classes.contentText}>
          Do you want to delete this item - {itemToDelete?.json_data ? (itemToDelete?.name || itemToDelete?.entity) : itemToDelete?.brand}?
        </div>
      </Modal>
    </>
  );
}